/* ============================================================
   VIDEO — <c-container-video>

   Er staan twee video's in de pagina, een voor desktop en een voor mobiel;
   de CSS toont er één (breekpunt 1024px, zie content-media.css). Dit script
   kiest de video op datzelfde breekpunt via matchMedia, niet op wat er op dat
   moment zichtbaar lijkt. Dat ging eerder mis op mobiel: het script draaide
   voordat de CSS de desktopvideo verborg, koos die verborgen desktopvideo en
   startte die. De mobiele video bleef dan stilstaan op zijn posterbeeld, met
   de afspeelknop eroverheen.

   De video's laden pas als ze in beeld komen (preload="none"), spelen
   gedempt, zonder geluid en in een lus, en starten vanzelf. Draait de
   bezoeker het scherm of wordt het venster breder of smaller, dan wisselt de
   video mee.

   Weigert de browser het automatisch afspelen (iOS in energiebesparingsmodus
   doet dat), dan probeert het script het opnieuw bij de eerste aanraking of
   scroll, en zodra het tabblad weer zichtbaar wordt.

   Wie beweging heeft uitgezet (prefers-reduced-motion) krijgt het
   posterbeeld en geen automatisch spelende video.

   Knoppen voor pauzeren en geluid zijn optioneel (instelling in het blok).
   Staan ze uit, dan staan ze niet in de pagina en doet dit script daar niets
   mee.
   ============================================================ */

(function () {
  'use strict';

  var BREEKPUNT = '(min-width: 1024px)';

  class ContainerVideo extends HTMLElement {
    connectedCallback() {
      this.desktop = this.querySelector('.video-desktop video');
      this.mobiel = this.querySelector('.video-mobile video');
      if (!this.desktop && !this.mobiel) return;

      this.playBtn = this.querySelector('[data-play-btn]');
      this.muteBtn = this.querySelector('[data-mute-btn]');
      this.hitArea = this.querySelector('[data-video-pause-hit]');
      this.rustig = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
      this.breed = window.matchMedia(BREEKPUNT);
      this.inBeeld = false;

      [this.desktop, this.mobiel].forEach((v) => {
        if (!v) return;
        // iOS speelt alleen automatisch af als de video echt gedempt is;
        // het attribuut alleen is niet in elke browser genoeg.
        v.muted = true;
        v.defaultMuted = true;
        v.playsInline = true;
        v.addEventListener('play', () => this.tekenKnoppen());
        v.addEventListener('pause', () => this.tekenKnoppen());
      });

      this.playBtn?.addEventListener('click', () => this.wisselAfspelen());
      this.muteBtn?.addEventListener('click', () => this.wisselGeluid());
      this.hitArea?.addEventListener('click', () => this.wisselAfspelen());

      this.kies();
      this.wissel = () => { this.kies(); if (this.inBeeld) this.start(); };
      if (this.breed.addEventListener) this.breed.addEventListener('change', this.wissel);
      else if (this.breed.addListener) this.breed.addListener(this.wissel);

      // pas laden en starten als hij (bijna) in beeld komt
      this.waarnemer = new IntersectionObserver((rijen) => {
        for (const rij of rijen) {
          if (!rij.isIntersecting) continue;
          this.inBeeld = true;
          this.start();
          this.waarnemer.disconnect();
        }
      }, { rootMargin: '200px' });
      this.waarnemer.observe(this);

      // tweede kans als de browser het afspelen weigerde
      this.opnieuw = () => { if (this.inBeeld && this.video && this.video.paused && !this.handmatigGepauzeerd) this.start(); };
      ['touchstart', 'scroll', 'click'].forEach((t) =>
        window.addEventListener(t, this.opnieuw, { passive: true }));
      document.addEventListener('visibilitychange', this.opnieuw);

      this.tekenKnoppen();
    }

    disconnectedCallback() {
      this.waarnemer?.disconnect();
      if (this.breed && this.wissel) {
        if (this.breed.removeEventListener) this.breed.removeEventListener('change', this.wissel);
        else if (this.breed.removeListener) this.breed.removeListener(this.wissel);
      }
      if (this.opnieuw) {
        ['touchstart', 'scroll', 'click'].forEach((t) => window.removeEventListener(t, this.opnieuw));
        document.removeEventListener('visibilitychange', this.opnieuw);
      }
    }

    /* De video bij het huidige breekpunt; de andere zetten we stil. Is er
       maar één, dan altijd die. */
    kies() {
      const gewenst = this.breed.matches ? (this.desktop || this.mobiel) : (this.mobiel || this.desktop);
      const andere = gewenst === this.desktop ? this.mobiel : this.desktop;
      if (andere && andere !== gewenst && !andere.paused) andere.pause();
      this.video = gewenst;
      this.tekenKnoppen();
    }

    start() {
      const v = this.video;
      if (!v || this.rustig) return;
      if (!v.dataset.geladen) { v.load(); v.dataset.geladen = '1'; }
      v.muted = true;
      const p = v.play();
      if (p && p.catch) p.catch(() => {});
    }

    wisselAfspelen() {
      if (!this.video) return;
      if (this.video.paused) { this.handmatigGepauzeerd = false; this.start(); }
      else { this.handmatigGepauzeerd = true; this.video.pause(); }
    }

    wisselGeluid() {
      if (!this.video) return;
      this.video.muted = !this.video.muted;
      this.tekenKnoppen();
    }

    tekenKnoppen() {
      if (!this.video || (!this.playBtn && !this.muteBtn && !this.hitArea)) return;
      const speelt = !this.video.paused;
      const gedempt = this.video.muted;
      this.zet('.video-btn__icon--play', !speelt);
      this.zet('.video-btn__icon--pause', speelt);
      this.zet('.video-btn__icon--unmuted', !gedempt);
      this.zet('.video-btn__icon--muted', gedempt);
      if (this.playBtn) this.playBtn.setAttribute('aria-label',
        speelt ? this.dataset.labelPause : this.dataset.labelPlay);
      if (this.muteBtn) this.muteBtn.setAttribute('aria-label',
        gedempt ? this.dataset.labelUnmute : this.dataset.labelMute);
      if (this.hitArea) this.hitArea.hidden = !speelt;
    }

    zet(selector, zichtbaar) {
      const el = this.querySelector(selector);
      if (el) el.hidden = !zichtbaar;
    }
  }

  if (!customElements.get('c-container-video')) {
    customElements.define('c-container-video', ContainerVideo);
  }

  /* c-media-container heeft geen eigen gedrag maar moet wel bestaan, anders
     behandelt de browser het als een onbekend inline-element. */
  if (!customElements.get('c-media-container')) {
    customElements.define('c-media-container', class extends HTMLElement {});
  }
})();
